import type { BlockNoteEditor } from "@blocknote/core";
import { COMMENT_THREAD_STYLE } from "./editorSchema";

export const getMarkThreadId = (mark: {
  type: { name: string };
  attrs: Record<string, unknown>;
}) => {
  if (mark.type.name === "comment") {
    return (mark.attrs.threadId as string | undefined) || null;
  }
  if (mark.type.name === COMMENT_THREAD_STYLE) {
    return (mark.attrs.stringValue as string | undefined) || null;
  }
  return null;
};

/** Contiguous spans owned by this thread id only — never the gap between them. */
export const findThreadRanges = (
  editor: BlockNoteEditor,
  threadId: string
): Array<{ from: number; to: number }> => {
  const ranges: Array<{ from: number; to: number }> = [];
  let current: { from: number; to: number } | null = null;

  editor.prosemirrorState.doc.descendants((node, pos) => {
    const matches =
      Boolean(node.isText && node.text) &&
      node.marks.some((mark) => getMarkThreadId(mark) === threadId);

    if (matches) {
      const from = pos;
      const to = pos + node.nodeSize;
      if (current && current.to === from) {
        current.to = to;
      } else {
        if (current) ranges.push(current);
        current = { from, to };
      }
      return true;
    }

    if (node.isText && current) {
      ranges.push(current);
      current = null;
    }
    return true;
  });

  if (current) ranges.push(current);
  return ranges;
};

export const findThreadRange = (
  editor: BlockNoteEditor,
  threadId: string,
  quote?: string
): { from: number; to: number } | null => {
  const ranges = findThreadRanges(editor, threadId);
  if (!ranges.length) return null;

  const needle = quote?.trim();
  if (needle) {
    const match = ranges.find((range) => {
      const text = editor.prosemirrorState.doc
        .textBetween(range.from, range.to, " ")
        .replace(/\s+/g, " ")
        .trim();
      return text === needle || text.includes(needle);
    });
    if (match) return match;
  }

  return ranges[0];
};

export const findThreadBounds = (
  editor: BlockNoteEditor,
  threadId: string
): { from: number; to: number } | null => {
  const ranges = findThreadRanges(editor, threadId);
  if (!ranges.length) return null;
  return {
    from: ranges[0].from,
    to: ranges[ranges.length - 1].to,
  };
};

export const getThreadQuote = (
  editor: BlockNoteEditor,
  threadId: string
): string | null => {
  const ranges = findThreadRanges(editor, threadId);
  if (!ranges.length) return null;
  const text = ranges
    .map((range) =>
      editor.prosemirrorState.doc.textBetween(range.from, range.to, " ")
    )
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
  return text || null;
};

export const threadIdAtPoint = (
  editor: BlockNoteEditor,
  clientX: number,
  clientY: number
): string | null => {
  const view = editor.prosemirrorView;
  if (!view) return null;

  const coords = view.posAtCoords({ left: clientX, top: clientY });
  if (!coords) return null;

  const ids = new Set<string>();
  const collect = (marks: readonly { type: { name: string }; attrs: Record<string, unknown> }[]) => {
    marks.forEach((mark) => {
      const threadId = getMarkThreadId(mark);
      if (threadId) ids.add(threadId);
    });
  };

  const $pos = editor.prosemirrorState.doc.resolve(coords.pos);
  collect($pos.marks());
  if ($pos.nodeBefore?.isText) collect($pos.nodeBefore.marks);

  if (!ids.size) return null;
  if (ids.size === 1) return Array.from(ids)[0];

  let tightestId = Array.from(ids)[0];
  let tightestSize = Number.POSITIVE_INFINITY;
  for (const id of ids) {
    const range = findThreadRange(editor, id);
    if (!range) continue;
    const size = range.to - range.from;
    if (size < tightestSize) {
      tightestId = id;
      tightestSize = size;
    }
  }
  return tightestId;
};

export const rangeHasOtherThread = (
  editor: BlockNoteEditor,
  from: number,
  to: number,
  threadId: string
) => {
  let other = false;
  editor.prosemirrorState.doc.nodesBetween(from, to, (node) => {
    if (!node.isText) return true;
    if (
      node.marks.some((mark) => {
        const id = getMarkThreadId(mark);
        return Boolean(id && id !== threadId);
      })
    ) {
      other = true;
    }
    return true;
  });
  return other;
};
